/**
 * Materialdateien liegen auf dem Gerät – der Server ist nur noch der
 * Abgleichspunkt zwischen Geräten (wie bei GoodNotes).
 *
 * Vorher war PocketBase die einzige Wahrheit: Öffnen hieß „erst die Liste vom
 * Server, dann jede Datei", Speichern hieß „warten, bis hochgeladen ist". Im
 * Schul-WLAN dauerte beides ewig oder scheiterte. Jetzt:
 *
 *   - Öffnen liest nur von hier. Fehlt eine Datei, wird sie geladen und bleibt.
 *   - Speichern schreibt hierher und ist damit fertig; hochgeladen wird im
 *     Hintergrund (fileSync.ts), so oft es eben braucht.
 *   - Jede Bearbeitungssitzung hinterlässt eine Version (höchstens
 *     `KEEP_VERSIONS`), die sich wiederherstellen lässt.
 *
 * `rev` wechselt bei jeder lokalen Änderung und steckt im Schlüssel des
 * Merge-Caches (topicPdf.ts) – ein fertig gebautes Arbeits-PDF passt damit
 * immer zum lokalen Stand, nicht zu einem womöglich veralteten Serverstand.
 */

import type { Material } from "@/types/models";

const DB_NAME = "lernapp-local";
const FILES = "files";
const VERSIONS = "versions";

export interface LocalFile {
  /** Material-ID; neu angelegte Dateien heißen bis zum Upload `local_…`. */
  id: string;
  topic: string;
  title: string;
  /** Dateiname (Formaterkennung der Pipeline, Name beim Upload). */
  name: string;
  type: string;
  position: number;
  created: string;
  blob: Blob;
  rev: string;
  /** `updated` des Serverstands, auf dem `blob` beruht ("" = noch nie oben). */
  remoteUpdated: string;
  /** `blob` ist noch nicht hochgeladen. */
  dirty: boolean;
}

export interface FileVersion {
  key: string;
  id: string;
  at: string;
  label: string;
  blob: Blob;
}

/** So viele Versionen bleiben je Datei liegen. */
const KEEP_VERSIONS = 8;
/**
 * Das Arbeits-PDF speichert beim Zeichnen alle paar Sekunden. Eine Version je
 * Speichervorgang hätte die ältesten Stände nach einer Minute verdrängt –
 * stattdessen eine je Sitzung: neue Version erst, wenn die letzte so alt ist.
 */
const SESSION_MS = 10 * 60_000;

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  dbPromise ??= new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      const files = db.createObjectStore(FILES, { keyPath: "id" });
      files.createIndex("topic", "topic");
      const versions = db.createObjectStore(VERSIONS, { keyPath: "key" });
      versions.createIndex("id", "id");
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => {
      dbPromise = null;
      reject(req.error);
    };
  });
  return dbPromise;
}

function done(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error ?? new Error("Speichern auf dem Gerät abgebrochen."));
  });
}

function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

const newRev = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

/** Ältere Versionen über `KEEP_VERSIONS` hinaus löschen (innerhalb derselben Transaktion). */
async function trimVersions(store: IDBObjectStore, id: string): Promise<void> {
  const all = await request(store.index("id").getAll(id) as IDBRequest<FileVersion[]>);
  all.sort((a, b) => b.at.localeCompare(a.at));
  for (const old of all.slice(KEEP_VERSIONS)) store.delete(old.key);
}

// Zufallsanteil im Schlüssel: zwei Versionen in derselben Millisekunde (Abgleich
// direkt nach dem Speichern) würden sich sonst gegenseitig überschreiben.
const versionKey = (id: string, at: string) => `${id}|${at}|${Math.random().toString(36).slice(2, 8)}`;

function putVersion(store: IDBObjectStore, file: LocalFile, label: string): void {
  const at = new Date().toISOString();
  store.put({ key: versionKey(file.id, at), id: file.id, at, label, blob: file.blob } satisfies FileVersion);
}

/** Unterscheidet Ein-Geräte-Änderungen (Zeichnen) von Fremdständen, die sofort als Version bleiben müssen. */
async function newestVersionAt(store: IDBObjectStore, id: string): Promise<string> {
  const all = await request(store.index("id").getAll(id) as IDBRequest<FileVersion[]>);
  return all.reduce((max, v) => (v.at > max ? v.at : max), "");
}

// Gemeinsamer Hörer für alle, die auf geänderte Dateien warten (Sync-Knopf).
const listeners = new Set<() => void>();
function changed(): void {
  for (const fn of listeners) fn();
}

export const localFiles = {
  onChange(fn: () => void): () => void {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },

  async get(id: string): Promise<LocalFile | null> {
    const db = await openDb();
    return (await request(db.transaction(FILES).objectStore(FILES).get(id))) ?? null;
  },

  async listTopic(topic: string): Promise<LocalFile[]> {
    const db = await openDb();
    return request(db.transaction(FILES).objectStore(FILES).index("topic").getAll(topic) as IDBRequest<LocalFile[]>);
  },

  /** Alles, was noch hochgeladen werden muss (geänderte und neu angelegte Dateien). */
  async pending(): Promise<LocalFile[]> {
    const db = await openDb();
    const all = await request(db.transaction(FILES).objectStore(FILES).getAll() as IDBRequest<LocalFile[]>);
    return all.filter((f) => f.dirty);
  },

  /**
   * Serverstand übernehmen (frisch geladen oder nach dem Upload bestätigt).
   * Eine noch nicht hochgeladene lokale Änderung wird dabei NIE überschrieben –
   * der Serverstand landet dann nur als Version daneben.
   */
  async putRemote(m: Material, blob: Blob): Promise<LocalFile> {
    const db = await openDb();
    const tx = db.transaction([FILES, VERSIONS], "readwrite");
    const files = tx.objectStore(FILES);
    const versions = tx.objectStore(VERSIONS);
    const old = (await request(files.get(m.id))) as LocalFile | undefined;
    let next: LocalFile;
    if (old?.dirty) {
      putVersion(versions, { ...old, blob }, "Stand von einem anderen Gerät");
      next = { ...old, remoteUpdated: m.updated };
    } else {
      if (old && old.remoteUpdated !== m.updated) putVersion(versions, old, "Vor dem Abgleich");
      next = {
        id: m.id,
        topic: m.topic,
        title: m.title,
        name: m.file,
        type: blob.type || m.type || "",
        position: m.position || 0,
        created: m.created ?? "",
        blob,
        rev: old && old.remoteUpdated === m.updated ? old.rev : newRev(),
        remoteUpdated: m.updated,
        dirty: false,
      };
    }
    files.put(next);
    await trimVersions(versions, m.id);
    await done(tx);
    return next;
  },

  /** Lokale Änderung (Anmerkungen, Wiederherstellen). Gilt sofort, Upload folgt im Hintergrund. */
  async saveLocal(id: string, blob: Blob, label = "Vor dem Bearbeiten", forceVersion = false): Promise<LocalFile> {
    const db = await openDb();
    const tx = db.transaction([FILES, VERSIONS], "readwrite");
    const files = tx.objectStore(FILES);
    const versions = tx.objectStore(VERSIONS);
    const old = (await request(files.get(id))) as LocalFile | undefined;
    if (!old) throw new Error("Datei ist auf diesem Gerät nicht vorhanden.");
    const last = await newestVersionAt(versions, id);
    if (forceVersion || !last || Date.now() - Date.parse(last) > SESSION_MS) putVersion(versions, old, label);
    // Ein annotiertes Bild wird zwangsläufig zum PDF (Striche lassen sich in
    // ein PNG nicht zurückschreiben) – Name und Typ ziehen mit.
    const becamePdf = blob.type === "application/pdf" && !/\.pdf$/i.test(old.name);
    const next: LocalFile = {
      ...old,
      blob,
      type: blob.type || old.type,
      name: becamePdf ? `${old.name.replace(/\.[^./]+$/, "")}.pdf` : old.name,
      rev: newRev(),
      dirty: true,
    };
    files.put(next);
    await trimVersions(versions, id);
    await done(tx);
    changed();
    return next;
  },

  /** Neue Datei nur auf dem Gerät anlegen – steht sofort zur Verfügung, Upload folgt. */
  async addLocal(topic: string, blob: Blob, name: string, title: string): Promise<LocalFile> {
    const file: LocalFile = {
      id: `local_${crypto.randomUUID()}`,
      topic,
      title,
      name,
      type: blob.type || "",
      position: Date.now(),
      created: new Date().toISOString(),
      blob,
      rev: newRev(),
      remoteUpdated: "",
      dirty: true,
    };
    const db = await openDb();
    const tx = db.transaction(FILES, "readwrite");
    tx.objectStore(FILES).put(file);
    await done(tx);
    changed();
    return file;
  },

  /**
   * Upload bestätigt. Nur „sauber", wenn sich seit Uploadbeginn lokal nichts
   * getan hat (`rev` gleich) – sonst bleibt die neuere Änderung ausstehend.
   */
  async markUploaded(id: string, rev: string, saved: Material): Promise<void> {
    const db = await openDb();
    const tx = db.transaction(FILES, "readwrite");
    const files = tx.objectStore(FILES);
    const cur = (await request(files.get(id))) as LocalFile | undefined;
    if (cur) {
      files.put({ ...cur, name: saved.file, remoteUpdated: saved.updated, dirty: cur.rev !== rev });
    }
    await done(tx);
    changed();
  },

  /** Neu angelegte Datei hat ihre Server-ID bekommen: Datei und Versionen umschlüsseln. */
  async rekey(localId: string, saved: Material, rev: string): Promise<void> {
    const db = await openDb();
    const tx = db.transaction([FILES, VERSIONS], "readwrite");
    const files = tx.objectStore(FILES);
    const versions = tx.objectStore(VERSIONS);
    const cur = (await request(files.get(localId))) as LocalFile | undefined;
    if (cur) {
      files.delete(localId);
      files.put({ ...cur, id: saved.id, name: saved.file, remoteUpdated: saved.updated, dirty: cur.rev !== rev });
      const old = await request(versions.index("id").getAll(localId) as IDBRequest<FileVersion[]>);
      for (const v of old) {
        versions.delete(v.key);
        versions.put({ ...v, id: saved.id, key: versionKey(saved.id, v.at) });
      }
    }
    await done(tx);
    rememberAlias(localId, saved.id);
    changed();
  },

  async remove(id: string): Promise<void> {
    const db = await openDb();
    const tx = db.transaction([FILES, VERSIONS], "readwrite");
    tx.objectStore(FILES).delete(id);
    const versions = tx.objectStore(VERSIONS);
    for (const v of await request(versions.index("id").getAll(id) as IDBRequest<FileVersion[]>)) {
      versions.delete(v.key);
    }
    await done(tx);
    changed();
  },

  async versions(id: string): Promise<FileVersion[]> {
    const db = await openDb();
    const all = await request(db.transaction(VERSIONS).objectStore(VERSIONS).index("id").getAll(id) as IDBRequest<FileVersion[]>);
    return all.sort((a, b) => b.at.localeCompare(a.at));
  },
};

/** Material-Datensatz für eine Datei, die es bisher nur auf diesem Gerät gibt. */
export function materialOf(f: LocalFile): Material {
  return {
    id: f.id,
    owner: "",
    topic: f.topic,
    title: f.title,
    file: f.name,
    type: f.type,
    size: f.blob.size,
    position: f.position,
    created: f.created,
    updated: f.remoteUpdated,
  };
}

// ---------------------------------------------------------------------------
// Alte lokale IDs: ein PDF-Verweis in einer Notiz (`pdf:local_…:3`), gesetzt
// bevor die Datei hochgeladen war, zeigt nach dem Upload sonst ins Leere.
// ---------------------------------------------------------------------------

const ALIAS_KEY = "lernapp.fileAliases";

function readAliases(): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(ALIAS_KEY) ?? "{}") as Record<string, string>;
  } catch {
    return {};
  }
}

function rememberAlias(localId: string, realId: string): void {
  try {
    localStorage.setItem(ALIAS_KEY, JSON.stringify({ ...readAliases(), [localId]: realId }));
  } catch {
    /* ignore */
  }
}

export function resolveFileId(id: string): string {
  return id.startsWith("local_") ? (readAliases()[id] ?? id) : id;
}

/** Bittet den Browser, die lokalen Dateien nicht bei Platzmangel wegzuräumen (iPad-PWA). */
export function requestPersistentStorage(): void {
  void navigator.storage?.persist?.().catch(() => false);
}
